/**
 * MockResultPage — 模擬試験結果 (FR-10 / FR-11 / UNC-01)
 * 科目別得点 + 合否目安（断定回避 #68）。色+アイコン #63。
 * passcut_group 単位でグループ判定。
 * 経験則 #68: 「合格を保証しません」免責表示必須
 * 経験則 #63: pass_likely/fail_likely は色+SVG+テキスト三重符号化
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { listMockExamResults } from '../api'
import { ApiClientError } from '../api/client'
import type { MockExamResult } from '../api/types'
import Icon from '../components/Icon'
import { PageLoader } from '../components/Spinner'

function PassJudgementBadge({ judgement }: { judgement?: string | null }) {
  const pass = judgement === 'pass_likely'
  return (
    <div
      className={[
        'flex items-center justify-center gap-2 rounded-md border px-4 py-3',
        pass ? 'border-correct bg-correct-bg' : 'border-incorrect bg-incorrect-bg',
      ].join(' ')}
    >
      {pass ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--status-correct-color)" aria-hidden="true">
          <circle cx="12" cy="12" r="11" />
          <path d="M7 12.5l3.5 3.5 6-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--status-incorrect-color)" aria-hidden="true">
          <circle cx="12" cy="12" r="11" />
          <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )}
      <span className={`text-lg font-bold ${pass ? 'text-correct-on' : 'text-incorrect-on'}`}>
        {pass ? '合格目安' : '不合格目安'}
      </span>
    </div>
  )
}

function SubjectRow({
  name,
  score,
  maxScore,
  correctCount,
  questionsCount,
  cleared,
}: {
  name: string
  score: number
  maxScore: number
  correctCount: number
  questionsCount: number
  cleared: boolean
}) {
  const ratio = maxScore > 0 ? score / maxScore : 0
  return (
    <div className="border-b border-border py-3">
      <div className="mb-1.5 flex justify-between">
        <span className="text-[13px] font-semibold text-text-primary">{name}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] text-text-secondary">
            {correctCount}/{questionsCount}問
          </span>
          {cleared ? (
            <span className="rounded-pill bg-correct-bg px-1.5 py-0.5 text-[11px] font-bold text-correct">基準クリア</span>
          ) : (
            <span className="rounded-pill bg-incorrect-bg px-1.5 py-0.5 text-[11px] font-bold text-incorrect">基準未満</span>
          )}
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-pill bg-surface-alt">
        <div
          className={`h-full rounded-pill ${cleared ? 'bg-correct' : 'bg-incorrect'}`}
          style={{ width: `${Math.min(Math.round(ratio * 100), 100)}%` }}
        />
      </div>
      <div className="mt-1 text-[11px] text-text-muted">
        {score}/{maxScore}点（{Math.round(ratio * 100)}%）
      </div>
    </div>
  )
}

export default function MockResultPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const stateResult = location.state?.result as MockExamResult | undefined

  const [result, setResult] = useState<MockExamResult | null>(stateResult ?? null)
  const [loading, setLoading] = useState(!stateResult)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (stateResult || !sessionId) return
    // IMPL-08 fix: 一覧は subject_results を含まない（summary）。見つかればそのまま表示。
    setLoading(true)
    listMockExamResults(undefined)
      .then((res) => {
        const found = res?.mock_exam_results?.find((r) => r.mock_exam_session_id === sessionId)
        if (found) setResult(found as MockExamResult)
        else setError('結果が見つかりません')
      })
      .catch((err) => {
        if (err instanceof ApiClientError) setError(err.message)
        else setError('結果の取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }, [sessionId, stateResult])

  if (loading) return <PageLoader label="採点中..." />

  if (error || !result) {
    return (
      <div className="mx-auto max-w-content p-screen">
        <div role="alert" className="alert-error mb-4">
          {error ?? '結果が見つかりません'}
        </div>
        <button onClick={() => navigate('/mock-exam')} className="btn-primary px-5">
          模擬試験トップへ
        </button>
      </div>
    )
  }

  const pct = result.score_ratio != null ? Math.round(result.score_ratio * 100) : null

  return (
    <div className="mx-auto max-w-content p-screen" data-testid="mock-result">
      <h1 className="mb-5 text-[22px] font-bold tracking-tight text-text-primary">模擬試験 結果</h1>

      {/* 合否目安 */}
      <section className="mb-4" data-testid="pass-judgement">
        <PassJudgementBadge judgement={result.pass_judgement} />
        <div className="mb-1 mt-3 text-center">
          <span className="font-numeric text-[28px] font-bold">
            {result.total_score} / {result.max_score}点
          </span>
          {pct != null && <span className="ml-2 text-base text-text-secondary">({pct}%)</span>}
        </div>
        {/* #68 断定回避 */}
        <p className="mt-1 text-center text-xs text-text-muted">
          ※合否目安です。合格を保証するものではありません。
        </p>
      </section>

      {/* 科目別得点 */}
      {result.subject_results && result.subject_results.length > 0 ? (
        <section className="mb-5">
          <div data-testid="subject-scores" className="card px-4 py-3.5">
            <div className="mb-3 text-[13px] font-bold text-text-secondary">科目別得点（合格基準: 各科目40%以上）</div>
            {result.subject_results.map((sub) => (
              <SubjectRow
                key={sub.subject_key}
                name={sub.name_ja ?? sub.subject_key ?? ''}
                score={sub.score ?? 0}
                maxScore={sub.max_score ?? 0}
                correctCount={sub.correct_count ?? 0}
                questionsCount={sub.questions_count ?? 0}
                cleared={!!sub.pass_cut_cleared}
              />
            ))}
          </div>
        </section>
      ) : (
        <section className="mb-5">
          <div data-testid="subject-scores-unavailable" className="card px-4 py-3.5 text-center">
            <div className="text-[13px] text-text-muted">
              科目別得点の詳細は模擬試験直後の結果画面でご確認ください。
            </div>
          </div>
        </section>
      )}

      {/* アクションボタン */}
      <div className="flex flex-col gap-2">
        <button data-testid="mock-to-review" onClick={() => navigate('/review')} className="btn-primary w-full">
          <Icon name="refresh" size={18} />
          間違いを復習する
        </button>
        <button data-testid="mock-retry" onClick={() => navigate('/mock-exam')} className="btn-secondary w-full">
          <Icon name="clipboard" size={18} />
          もう一度受ける
        </button>
        <button data-testid="mock-to-stats" onClick={() => navigate('/stats')} className="btn-ghost w-full text-text-secondary">
          <Icon name="chart" size={16} />
          スコア推移を確認
        </button>
      </div>
    </div>
  )
}
